import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MapPin, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * AddressAutocomplete
 * - Free-text input with debounced OSM Nominatim suggestions (SD County bounded)
 * - onSelect(suggestion) returns { full_address, city, county, state, lat, lon, jurisdiction }
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing an address…",
  variant = "default", // "default" | "mega"
  testid = "address-autocomplete",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const fetchSuggestions = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.cancel?.();

    if (!q || q.trim().length < 3) {
      setSuggestions([]); setOpen(false); return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/address/autocomplete`, { params: { q } });
        setSuggestions(data.suggestions || []);
        setOpen(true);
        setHighlighted(-1);
      } catch (e) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    setSelected(null);
    fetchSuggestions(v);
  };

  const pick = (s) => {
    setSelected(s);
    setOpen(false);
    onChange?.(s.full_address);
    onSelect?.(s);
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && highlighted >= 0) { e.preventDefault(); pick(suggestions[highlighted]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const isMega = variant === "mega";

  return (
    <div ref={wrapperRef} className="relative" data-testid={testid}>
      <div className="relative">
        <MapPin
          className={`absolute ${isMega ? "left-0 top-3" : "left-3 top-1/2 -translate-y-1/2"} w-4 h-4`}
          strokeWidth={1.5}
          style={{ color: "var(--bw-text-tertiary)" }}
        />
        <input
          type="text"
          value={value || ""}
          onChange={handleChange}
          onKeyDown={handleKey}
          onFocus={() => value?.length >= 3 && setOpen(true)}
          placeholder={placeholder}
          className={isMega ? "bw-input-mega pl-7" : "bw-input pl-9"}
          autoComplete="off"
          spellCheck={false}
          data-testid={`${testid}-input`}
        />
        {loading && (
          <Loader2 className={`absolute ${isMega ? "right-0 top-3" : "right-3 top-1/2 -translate-y-1/2"} w-4 h-4 animate-spin`}
            strokeWidth={1.5} style={{ color: "var(--bw-text-tertiary)" }} />
        )}
      </div>

      {selected && !open && (
        <div
          className="mt-2 inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 border"
          style={{
            color: selected.jurisdiction ? "var(--bw-success)" : "var(--bw-warning)",
            borderColor: selected.jurisdiction ? "var(--bw-success)" : "var(--bw-warning)",
          }}
          data-testid={`${testid}-jurisdiction`}
        >
          {selected.jurisdiction ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          {selected.jurisdiction || "Outside San Diego County"}
        </div>
      )}

      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 mt-2 left-0 right-0 bw-card overflow-hidden shadow-lg"
          style={{ backgroundColor: "#ffffff" }}
          data-testid={`${testid}-dropdown`}
        >
          {suggestions.map((s, i) => (
            <button
              key={s.place_id || i}
              type="button"
              onClick={() => pick(s)}
              onMouseEnter={() => setHighlighted(i)}
              className={`w-full text-left px-4 py-3 border-b last:border-b-0 transition-colors ${i === highlighted ? "bg-tan" : ""}`}
              style={{
                borderColor: "var(--bw-border)",
                backgroundColor: i === highlighted ? "var(--bw-bg-tertiary)" : "transparent",
              }}
              data-testid={`${testid}-option-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate" style={{ color: "var(--bw-text-primary)" }}>
                    {s.primary}
                  </div>
                  <div className="text-xs mt-0.5 truncate" style={{ color: "var(--bw-text-secondary)" }}>
                    {s.secondary}
                  </div>
                </div>
                {s.jurisdiction && (
                  <span
                    className="flex-shrink-0 text-[10px] font-bold uppercase tracking-widest px-2 py-1 border"
                    style={{ color: "var(--bw-brand)", borderColor: "var(--bw-brand)" }}
                  >
                    {s.jurisdiction.replace("City of ", "").replace("County of San Diego (Unincorporated)", "SD County")}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {open && !loading && suggestions.length === 0 && value?.length >= 3 && (
        <div className="absolute z-50 mt-2 left-0 right-0 bw-card p-4 text-sm" data-testid={`${testid}-empty`}>
          <div className="font-semibold mb-1" style={{ color: "var(--bw-text-primary)" }}>
            No suggestions right now — that's okay.
          </div>
          <div style={{ color: "var(--bw-text-secondary)" }}>
            Just type your full address (like <span className="font-mono text-xs">&quot;1234 Main St, San Diego, CA&quot;</span>) and click Continue. We'll detect the jurisdiction automatically.
          </div>
        </div>
      )}
    </div>
  );
}
